import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, UserPlus, Building2, RefreshCw, History, 
  X, Check, Mail, Shield, ShieldAlert, Calendar, 
  ChevronRight, AlertCircle, Trash2, ArrowLeft 
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Profiles, Companies, RegistrationInvites } from '../services/employeeApi';
import { authClient } from '../lib/authClient';
import NewCompanyWithManager from '../components/admin/NewCompanyWithManager';
import PlanPresetsAdmin from '../components/admin/PlanPresetsAdmin';

const COMPANY_NONE = '__NONE__';

function EmployeeManagementPage() {
  const { isDarkMode, themeClasses: baseThemeClasses } = useTheme();
  const navigate = useNavigate();

  // --- LOCAL LANGUAGE STATE ---
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'english';
  });
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';
  const isRTL = direction === 'rtl';

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    document.documentElement.dir = direction;
    document.documentElement.lang = language === 'hebrew' ? 'he' : 'en';
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, [direction, language]);

  // --- PREMIUM DESIGN TOKENS ---
  const themeClasses = {
    ...baseThemeClasses,
    inputField: `w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-all duration-200 focus:border-forest/40 focus:ring-2 focus:ring-forest/20 ${isDarkMode ? 'bg-canvas/50 border-line text-ink placeholder-ink-muted' : 'bg-canvas border-line text-ink placeholder-ink-muted'}`,
    select: `w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-all duration-200 focus:border-forest/40 focus:ring-2 focus:ring-forest/20 appearance-none bg-no-repeat pr-10 ${isDarkMode ? 'bg-canvas/50 border-line text-ink [&_option]:bg-surface' : 'bg-canvas border-line text-ink'}`,
    btnPrimary: `px-5 py-2.5 rounded-lg font-medium bg-forest-ondark text-white dark:text-canvas-dark hover:bg-forest-ondark/90   active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none transition-all duration-200`,
    btnSecondary: `px-5 py-2.5 rounded-lg font-medium border border-line dark:border-line bg-surface dark:bg-surface text-ink dark:text-ink hover:bg-canvas dark:hover:bg-moss/50  focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 transition-all duration-200`,
  };

  const cardBase = `${themeClasses.bgCard}  rounded-lg border border-line dark:border-line   transition-shadow duration-300`;

  // --- TRANSLATIONS ---
  const translations = {
    english: {
      'loading': 'Loading…',
      'error': 'Error',
      'refresh': 'Refresh',

      'page.title': 'Team & Invites',
      'page.subtitle': 'Invite staff, manage roles, and assign team members to your company',

      'access.restricted': 'Access restricted',
      'access.message': 'You do not have permission to view this page.',

      'invites.title': 'Invitations',
      'invites.active': 'active',
      'invites.activeStatus': 'Active',
      'invites.hideHistory': 'Hide history',
      'invites.showHistory': 'Show history',
      'invites.email': 'Email',
      'invites.role': 'Role',
      'invites.company': 'Company',
      'invites.expires': 'Expires in (hours)',
      'invites.notes': 'Notes (optional)',
      'invites.create': 'Send invite',
      'invites.code': 'Code',
      'invites.status': 'Status',
      'invites.created': 'Created',
      'invites.actions': 'Actions',
      'invites.revoke': 'Revoke',
      'invites.revoked': 'Revoked',
      'invites.used': 'Used',
      'invites.expired': 'Expired',

      'role.employee': 'Employee',
      'role.company_manager': 'Company Manager',
      'role.sys_admin': 'System Admin',

      'team.title': 'Team Members',
      'team.name': 'Name',
      'team.role': 'Role',
      'team.company': 'Company',
      'team.actions': 'Actions',
      'team.load10More': 'Load 10 more',
      'team.showAll': 'Show all',

      'companies.title': 'Companies',
      'companies.none': 'No company',
      'companies.name': 'Company name',
      'companies.create': 'Create company',
    },
    hebrew: {
      'loading': 'טוען…',
      'error': 'שגיאה',
      'refresh': 'רענן',

      'page.title': 'צוות והזמנות',
      'page.subtitle': 'הזמן עובדים, נהל תפקידים והקצה חברי צוות לחברה שלך',

      'access.restricted': 'הגישה מוגבלת',
      'access.message': 'אין לך הרשאה לצפות בעמוד זה.',

      'invites.title': 'הזמנות',
      'invites.active': 'פעילות',
      'invites.activeStatus': 'פעיל',
      'invites.hideHistory': 'הסתר היסטוריה',
      'invites.showHistory': 'הצג היסטוריה',
      'invites.email': 'אימייל',
      'invites.role': 'תפקיד',
      'invites.company': 'חברה',
      'invites.expires': 'תוקף (שעות)',
      'invites.notes': 'הערות (לא חובה)',
      'invites.create': 'שלח הזמנה',
      'invites.code': 'קוד',
      'invites.status': 'סטטוס',
      'invites.created': 'נוצר',
      'invites.actions': 'פעולות',
      'invites.revoke': 'בטל',
      'invites.revoked': 'בוטל',
      'invites.used': 'נוצל',
      'invites.expired': 'פג תוקף',

      'role.employee': 'עובד',
      'role.company_manager': 'מנהל חברה',
      'role.sys_admin': 'מנהל מערכת',

      'team.title': 'חברי צוות',
      'team.name': 'שם',
      'team.role': 'תפקיד',
      'team.company': 'חברה',
      'team.actions': 'פעולות',
      'team.load10More': 'טען 10 נוספים',
      'team.showAll': 'הצג הכל',

      'companies.title': 'חברות',
      'companies.none': 'ללא חברה',
      'companies.name': 'שם החברה',
      'companies.create': 'צור חברה',
    },
  };

  const t = (key) => {
    const dict = translations[language] || translations.english;
    return dict[key] ?? translations.english[key] ?? key;
  };

  // --- PERMISSION & ACCESS CONTROL ---
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- DATA STATE ---
  const [profiles, setProfiles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [invites, setInvites] = useState([]);

  // --- LOADING STATES ---
  const [refreshing, setRefreshing] = useState(false);
  const [updatingProfile, setUpdatingProfile] = useState({});
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [invitesError, setInvitesError] = useState(null);

  // --- INVITE FORM STATE ---
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'employee',
    company_id: COMPANY_NONE,
    expires_in_hours: '',
    notes: '',
  });
  const [showInviteHistory, setShowInviteHistory] = useState(false);

  const [visibleProfileCount, setVisibleProfileCount] = useState(10);

  // --- INITIALIZATION & PERMISSION CHECK ---
  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        setError(null);

        const session = await authClient.getSession();
        if (!session?.user?.id) {
          throw new Error('Not authenticated');
        }

        const userId = session.user.id;
        const myProfile = await Profiles.getMyProfile(userId);
        setCurrentUser(myProfile);

        const userRole = myProfile?.role;
        if (userRole !== 'sys_admin' && userRole !== 'company_manager') {
          setIsAuthorized(false);
          setLoading(false);
          return;
        }

        setIsAuthorized(true);
        await loadData(myProfile);
      } catch (err) {
        console.error('Error initializing:', err);
        setError(err.message || 'Failed to load page');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  // --- DATA LOADING ---
  const loadData = async (initialProfile) => {
    const profile = initialProfile ?? currentUser;
    try {
      setRefreshing(true);
      setError(null);

      const [profilesData, companiesData] = await Promise.all([
        Profiles.list(),
        Companies.list(),
      ]);

      setProfiles(profilesData || []);
      setCompanies(companiesData || []);

      if (profile?.role === 'sys_admin' || profile?.role === 'company_manager') {
        try {
          const invitesData = await RegistrationInvites.list(profile.id);
          setInvites(invitesData || []);
          setInvitesError(null);
        } catch (err) {
          console.error('Error loading invites:', err);
          setInvitesError(err.message || 'Failed to load invites');
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setRefreshing(false);
    }
  };

  // --- FILTERED DATA ---
  const visibleProfiles = useMemo(() => {
    if (!currentUser) return [];
    if (currentUser.role === 'sys_admin') return profiles;
    if (currentUser.role === 'company_manager') {
      return profiles.filter(
        p => p.company_id === currentUser.company_id && p.role !== 'sys_admin'
      );
    }
    return [];
  }, [profiles, currentUser]);

  const visibleCompanies = useMemo(() => {
    if (!currentUser) return [];
    if (currentUser.role === 'sys_admin') return companies;
    if (currentUser.role === 'company_manager') {
      return companies.filter(c => c.id === currentUser.company_id);
    }
    return [];
  }, [companies, currentUser]);

  // --- HELPER FUNCTIONS ---
  const formatDateTime = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleDateString(language === 'hebrew' ? 'he-IL' : 'en-US', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return '—';
    }
  };

  const getInviteStatus = (invite) => {
    if (invite.revoked_at) return { label: 'Revoked', variant: 'destructive' };
    if (invite.used_at) return { label: 'Used', variant: 'secondary' };
    if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
      return { label: 'Expired', variant: 'secondary' };
    }
    return { label: 'Active', variant: 'success' };
  };

  const isInviteActive = (invite) => {
    return !invite.revoked_at && !invite.used_at && 
           (!invite.expires_at || new Date(invite.expires_at) >= new Date());
  };

  const visibleInvites = useMemo(() => {
    if (showInviteHistory) return invites;
    return invites.filter(isInviteActive);
  }, [invites, showInviteHistory]);

  const activeInvitesCount = useMemo(() => {
    return invites.filter(isInviteActive).length;
  }, [invites]);

  const companyMap = useMemo(() => {
    const map = new Map();
    companies.forEach(c => map.set(c.id, c.name));
    return map;
  }, [companies]);

  const profileMap = useMemo(() => {
    const map = new Map();
    profiles.forEach(p => map.set(p.id, p));
    return map;
  }, [profiles]);

  // --- INVITE HANDLERS ---
  const handleCreateInvite = async () => {
    if (!inviteForm.email.trim()) {
      alert('Email is required');
      return;
    }

    const isCompanyManager = currentUser?.role === 'company_manager';
    const companyId = isCompanyManager
      ? currentUser.company_id
      : (inviteForm.company_id === COMPANY_NONE ? null : inviteForm.company_id);
    const role = isCompanyManager && !['employee', 'company_manager'].includes(inviteForm.role)
      ? 'employee'
      : inviteForm.role;

    try {
      setCreatingInvite(true);
      const payload = {
        email: inviteForm.email.trim().toLowerCase(),
        role,
        company_id: companyId,
        expires_in_hours: inviteForm.expires_in_hours ? parseInt(inviteForm.expires_in_hours) : null,
        notes: inviteForm.notes.trim() || null,
      };

      const result = await RegistrationInvites.create(payload, currentUser?.id);
      alert(`Invite created! Code: ${result.code}`);
      setInviteForm({ email: '', role: 'employee', company_id: COMPANY_NONE, expires_in_hours: '', notes: '' });
      await loadData();
    } catch (err) {
      console.error('Error creating invite:', err);
      alert(`Error: ${err.message || 'Failed to create invite'}`);
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleRevokeInvite = async (code) => {
    if (!window.confirm('Are you sure you want to revoke this invite?')) return;
    try {
      await RegistrationInvites.revoke(code, currentUser?.id);
      alert('Invite revoked successfully');
      await loadData();
    } catch (err) {
      console.error('Error revoking invite:', err);
      alert(`Error: ${err.message || 'Failed to revoke invite'}`);
    }
  };

  // --- PROFILE HANDLERS ---
  const handleProfileUpdate = async (profileId, patch, successMessage = 'Profile updated successfully') => {
    try {
      setUpdatingProfile(prev => ({ ...prev, [profileId]: true }));
      const updated = await Profiles.update(profileId, patch);
      setProfiles(prev => prev.map(p => p.id === profileId ? updated : p));
      alert(successMessage);
    } catch (err) {
      console.error('Error updating profile:', err);
      alert(`Error: ${err.message || 'Failed to update profile'}`);
    } finally {
      setUpdatingProfile(prev => ({ ...prev, [profileId]: false }));
    }
  };

  const handleRoleChange = async (profileId, nextRole) => {
    const isCompanyManager = currentUser?.role === 'company_manager';
    const targetProfile = profileMap.get(profileId);
    if (isCompanyManager && targetProfile?.role === 'sys_admin') {
      alert('You cannot modify sys_admin roles');
      return;
    }
    if (isCompanyManager && nextRole === 'sys_admin') {
      alert('You cannot promote users to sys_admin');
      return;
    }
    await handleProfileUpdate(profileId, { role: nextRole }, 'Role updated successfully');
  };

  const handleCompanyChange = async (profileId, nextCompanyId) => {
    const isCompanyManager = currentUser?.role === 'company_manager';
    const targetProfile = profileMap.get(profileId);
    if (isCompanyManager && targetProfile?.role === 'sys_admin') {
      alert('You cannot modify sys_admin company assignments');
      return;
    }
    if (isCompanyManager && nextCompanyId !== currentUser.company_id) {
      alert('You can only assign users to your own company');
      return;
    }
    const companyId = nextCompanyId === COMPANY_NONE ? null : nextCompanyId;
    await handleProfileUpdate(profileId, { company_id: companyId }, 'Company assignment updated successfully');
  };

  const displayedProfiles = useMemo(() => {
    return visibleProfiles.slice(0, visibleProfileCount);
  }, [visibleProfiles, visibleProfileCount]);

  const isSysAdmin = currentUser?.role === 'sys_admin';
  const isCompanyManager = currentUser?.role === 'company_manager';

  // --- RENDER ---
  if (loading) {
    return (
      <div className={`min-h-screen ${themeClasses.bgPrimary} transition-colors duration-300 flex items-center justify-center`} dir={direction}>
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 text-forest-ondark animate-spin" />
          <div className={`${themeClasses.textPrimary} text-sm font-semibold tracking-wide animate-pulse`}>{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className={`min-h-screen ${themeClasses.bgPrimary} transition-colors duration-300 flex items-center justify-center p-4`} dir={direction}>
        <div className={`${cardBase} p-6 sm:p-8 text-center w-full max-w-md`}>
          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert size={24} />
          </div>
          <h2 className={`text-xl font-bold ${themeClasses.textPrimary} mb-2`}>{t('access.restricted')}</h2>
          <p className={`${themeClasses.textMuted} text-sm sm:text-base mb-6`}>{t('access.message')}</p>
          <button onClick={() => navigate(-1)} className={themeClasses.btnSecondary + ' w-full'}>
            <ArrowLeft size={16} /> Go Back
          </button>
        </div>
      </div>
    );
  }

  if (error && !refreshing) {
    return (
      <div className={`min-h-screen ${themeClasses.bgPrimary} transition-colors duration-300 flex items-center justify-center p-4`} dir={direction}>
        <div className={`${cardBase} p-6 sm:p-8 text-center w-full max-w-md`}>
          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={24} />
          </div>
          <p className={`text-sm sm:text-base ${themeClasses.textPrimary} mb-6`}>{t('error')}: {error}</p>
          <button onClick={loadData} className={themeClasses.btnPrimary + ' w-full'}>
            <RefreshCw size={16} /> {t('refresh')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${themeClasses.bgPrimary} transition-colors duration-300 pb-12 font-sans`} dir={direction}>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className={`text-3xl sm:text-4xl font-semibold tracking-tight ${themeClasses.textPrimary} mb-2`}>
              {t('page.title')}
            </h1>
            <p className={`text-base ${themeClasses.textSecondary}`}>
              {t('page.subtitle')}
            </p>
          </div>
          <button
            onClick={loadData}
            disabled={refreshing}
            className={themeClasses.btnPrimary + ' sm:w-auto'}
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            {t('refresh')}
          </button>
        </div>

        {/* Section 1: Registration Invitations */}
        {(isSysAdmin || isCompanyManager) && (
          <div className={`${cardBase} p-5 sm:p-6 mb-8`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <h2 className={`text-xl font-bold ${themeClasses.textPrimary} flex items-center gap-3`}>
                <div className="p-2 bg-forest-faint dark:bg-forest/15 rounded-lg text-forest-ondark dark:text-forest-ondark">
                  <UserPlus size={20} />
                </div>
                <span>{t('invites.title')}</span>
                {activeInvitesCount > 0 && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-forest-faint text-forest-ondark dark:bg-forest/15 dark:text-forest">
                    {activeInvitesCount} {t('invites.active')}
                  </span>
                )}
              </h2>
              <button
                onClick={() => setShowInviteHistory(!showInviteHistory)}
                className={`text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${themeClasses.textMuted} hover:${themeClasses.textPrimary}`}
              >
                <History size={16} />
                {showInviteHistory ? t('invites.hideHistory') : t('invites.showHistory')}
              </button>
            </div>

            {invitesError && (
              <div className="mb-4 p-4 rounded-lg text-sm bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200/40 flex items-center gap-2">
                <AlertCircle size={16} /> {t('error')} : {invitesError}
              </div>
            )}

            {/* Create Invite Form Controls */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
              <input
                type="email"
                placeholder={t('invites.email')}
                value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                className={themeClasses.inputField}
              />
              <div className="relative">
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                  className={themeClasses.select}
                >
                  <option value="employee">{t('role.employee')}</option>
                  <option value="company_manager">{t('role.company_manager')}</option>
                  {isSysAdmin && <option value="sys_admin">{t('role.sys_admin')}</option>}
                </select>
                <ChevronRight size={16} className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 text-ink-muted rotate-90 pointer-events-none`} />
              </div>
              {isSysAdmin ? (
                <div className="relative">
                  <select
                    value={inviteForm.company_id}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, company_id: e.target.value }))}
                    className={themeClasses.select}
                  >
                    <option value={COMPANY_NONE}>{t('companies.none')}</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <ChevronRight size={16} className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 text-ink-muted rotate-90 pointer-events-none`} />
                </div>
              ) : (
                <div className={`w-full px-4 py-2.5 text-sm ${themeClasses.textSecondary} bg-canvas dark:bg-canvas/50 border border-line dark:border-line rounded-lg flex items-center `}>
                  {currentUser?.company_id ? (companyMap.get(currentUser.company_id) || t('invites.company')) : t('companies.none')}
                </div>
              )}
              <input
                type="number"
                placeholder={t('invites.expires')}
                value={inviteForm.expires_in_hours}
                onChange={(e) => setInviteForm(prev => ({ ...prev, expires_in_hours: e.target.value }))}
                className={themeClasses.inputField}
              />
              <button onClick={handleCreateInvite} disabled={creatingInvite} className={themeClasses.btnPrimary}>
                {creatingInvite ? <RefreshCw size={16} className="animate-spin" /> : <UserPlus size={16} />}
                {t('invites.create')}
              </button>
            </div>
            
            <textarea
              placeholder={t('invites.notes')}
              value={inviteForm.notes}
              onChange={(e) => setInviteForm(prev => ({ ...prev, notes: e.target.value }))}
              className={`${themeClasses.inputField} resize-none mb-6`}
              rows="2"
            />

            {/* Invites Data Table */}
            <div className="overflow-x-auto custom-scrollbar border border-line dark:border-line rounded-lg">
              <table className="w-full text-left border-collapse min-w-[800px]" dir={direction}>
                <thead>
                  <tr className="border-b border-line dark:border-line bg-canvas/50 dark:bg-surface/30">
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted">{t('invites.email')}</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted">{t('invites.code')}</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted">{t('invites.role')}</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted">{t('invites.company')}</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted text-center">{t('invites.status')}</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted">{t('invites.created')}</th>
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted text-right">{t('invites.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line dark:divide-line">
                  {visibleInvites.map(invite => {
                    const status = getInviteStatus(invite);
                    return (
                      <tr key={invite.id} className="hover:bg-canvas dark:hover:bg-surface/20 transition-colors">
                        <td className={`px-5 py-4 text-sm font-semibold ${themeClasses.textPrimary} max-w-[180px] truncate`} title={invite.email}>{invite.email}</td>
                        <td className="px-5 py-4 font-mono text-sm text-forest-ondark dark:text-forest-ondark font-medium">{invite.code}</td>
                        <td className={`px-5 py-4 text-sm ${themeClasses.textSecondary}`}>{t(`role.${invite.role}`)}</td>
                        <td className={`px-5 py-4 text-sm ${themeClasses.textSecondary} truncate max-w-[120px]`}>
                          {invite.company_id ? (companyMap.get(invite.company_id) || '—') : t('companies.none')}
                        </td>
                        <td className="px-5 py-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                            status.variant === 'destructive' 
                              ? 'bg-red-50 text-red-700 border border-red-200/40 dark:bg-red-900/20 dark:text-red-300'
                              : status.variant === 'secondary'
                              ? 'bg-moss text-ink-muted dark:bg-surface dark:text-ink-muted'
                              : 'bg-forest-faint text-forest-ondark border border-forest/40 dark:bg-forest/15 dark:text-forest'
                          }`}>
                            {t(`invites.${status.label.toLowerCase()}`)}
                          </span>
                        </td>
                        <td className={`px-5 py-4 text-xs ${themeClasses.textMuted}`}>{formatDateTime(invite.created_at)}</td>
                        <td className="px-5 py-4 text-right">
                          {status.label === 'Active' && (
                            <button
                              onClick={() => handleRevokeInvite(invite.code)}
                              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-semibold text-sm transition-colors"
                            >
                              {t('invites.revoke')}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {visibleInvites.length === 0 && (
                <div className="p-8 text-center">
                  <p className={themeClasses.textMuted}>{language === 'hebrew' ? 'לא נמצאו הזמנות' : 'No invitations found'}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section 2: Team Roles Management */}
        <div className={`${cardBase} p-5 sm:p-6 mb-8`}>
          <h2 className={`text-xl font-bold ${themeClasses.textPrimary} mb-6 flex items-center gap-3`}>
            <div className="p-2 bg-forest-faint dark:bg-forest/15 rounded-lg text-forest-ondark dark:text-forest-ondark">
              <Users size={20} />
            </div>
            <span>{t('team.title')}</span>
          </h2>
          <div className="overflow-x-auto custom-scrollbar border border-line dark:border-line rounded-lg">
            <table className="w-full text-left border-collapse min-w-[600px]" dir={direction}>
              <thead>
                <tr className="border-b border-line dark:border-line bg-canvas/50 dark:bg-surface/30">
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted">{t('team.name')}</th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted">{t('team.role')}</th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted">{t('team.company')}</th>
                  <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted text-right">{t('team.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line dark:divide-line">
                {displayedProfiles.map(profile => {
                  const canEdit = !(isCompanyManager && profile.role === 'sys_admin');
                  const isUpdating = updatingProfile[profile.id];
                  
                  return (
                    <tr key={profile.id} className="hover:bg-canvas dark:hover:bg-surface/20 transition-colors">
                      <td className={`px-5 py-4 text-sm font-semibold ${themeClasses.textPrimary}`}>{profile.name || '—'}</td>
                      <td className="px-5 py-4">
                        {canEdit ? (
                          <div className="relative max-w-[200px]">
                            <select
                              value={profile.role || ''}
                              onChange={(e) => handleRoleChange(profile.id, e.target.value)}
                              disabled={isUpdating}
                              className={`${themeClasses.select} py-1.5 px-3 text-xs`}
                            >
                              <option value="employee">{t('role.employee')}</option>
                              <option value="company_manager">{t('role.company_manager')}</option>
                              {isSysAdmin && <option value="sys_admin">{t('role.sys_admin')}</option>}
                            </select>
                            <ChevronRight size={14} className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-ink-muted rotate-90 pointer-events-none`} />
                          </div>
                        ) : (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border border-purple-200/40`}>
                            {t(`role.${profile.role}`)}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        {canEdit ? (
                          <div className="relative max-w-[200px]">
                            <select
                              value={profile.company_id || COMPANY_NONE}
                              onChange={(e) => handleCompanyChange(profile.id, e.target.value)}
                              disabled={isUpdating}
                              className={`${themeClasses.select} py-1.5 px-3 text-xs`}
                            >
                              <option value={COMPANY_NONE}>{t('companies.none')}</option>
                              {visibleCompanies.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                            <ChevronRight size={14} className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-ink-muted rotate-90 pointer-events-none`} />
                          </div>
                        ) : (
                          <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                            {profile.company_id ? (companyMap.get(profile.company_id) || '—') : t('companies.none')}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {isUpdating && (
                          <RefreshCw className="w-4 h-4 text-forest-ondark animate-spin ml-auto" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {visibleProfiles.length === 0 && (
              <div className="p-8 text-center">
                <p className={themeClasses.textMuted}>{language === 'hebrew' ? 'לא נמצאו חברי צוות' : 'No profiles found'}</p>
              </div>
            )}
            {visibleProfiles.length > visibleProfileCount && (
              <div className="flex flex-wrap items-center justify-center gap-2 p-4 border-t border-line dark:border-line">
                <button
                  type="button"
                  onClick={() => setVisibleProfileCount(prev => prev + 10)}
                  className={themeClasses.btnSecondary + ' !py-2 !text-xs'}
                >
                  {t('team.load10More')}
                </button>
                <button
                  type="button"
                  onClick={() => setVisibleProfileCount(visibleProfiles.length)}
                  className={themeClasses.btnPrimary + ' !py-2 !text-xs'}
                >
                  {t('team.showAll')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Section 3: Company Management */}
        {isSysAdmin && (
          <div className={`${cardBase} p-5 sm:p-6`}>
            <h2 className={`text-xl font-bold ${themeClasses.textPrimary} mb-6 flex items-center gap-3`}>
              <div className="p-2 bg-forest-faint dark:bg-forest/15 rounded-lg text-forest-ondark dark:text-forest-ondark">
                <Building2 size={20} />
              </div>
              <span>{t('companies.title')}</span>
            </h2>
            
            {/* New company + its manager's signup invite (replaces the name-only form) */}
            <div className="mb-6">
              <NewCompanyWithManager onCreated={() => loadData()} />
            </div>

            {/* Companies List Table */}
            <div className="overflow-x-auto border border-line dark:border-line rounded-lg">
              <table className="w-full text-left border-collapse" dir={direction}>
                <thead>
                  <tr className="border-b border-line dark:border-line bg-canvas/50 dark:bg-surface/30">
                    <th className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted">{t('companies.name')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line dark:divide-line">
                  {visibleCompanies.map(company => (
                    <tr key={company.id} className="hover:bg-canvas dark:hover:bg-surface/20 transition-colors">
                      <td className={`px-5 py-4 text-sm font-semibold ${themeClasses.textPrimary}`}>{company.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {visibleCompanies.length === 0 && (
                <div className="p-8 text-center">
                  <p className={themeClasses.textMuted}>{language === 'hebrew' ? 'לא נמצאו חברות' : 'No companies found'}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section 4: Plan presets (sys_admin) */}
        {isSysAdmin && (
          <div className="mt-8">
            <PlanPresetsAdmin />
          </div>
        )}
      </main>
    </div>
  );
}

export default EmployeeManagementPage;